/* eslint-disable consistent-return */

import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  Observable,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { sentryApolloLink } from '../observability/apolloLink';
import { getGraphqlUrl } from '../utils/backendOverride';
import { getActiveSedeId } from '../utils/partnerAccess/partnerStorage';
import { onDelegatedTokenChange, peekEffectiveToken } from '../utils/partnerAccess/effectiveToken';
import {
  SEDE_LOST_REASONS,
  discardRejectedDelegatedToken,
  getEffectiveTokenForRequest,
  handleAuthFailureInSedeMode,
  handleSedeAccessLost,
} from '../utils/partnerAccess/sedeSession';

const httpLink = new HttpLink({
  // Resolved per request: the A/B company is routed to the sin-Prisma backend.
  // Subscriptions stay on the env backend on purpose (A/B covers REST +
  // GraphQL HTTP only; both backends share the same MongoDB).
  uri: () => getGraphqlUrl(),
});

// Partner access: in sede mode HTTP and WS authenticate with a delegated token
// (minted on demand); otherwise with the normal JWT, as always.
//
// The server freezes the token into the connection, so a socket has to be
// reconnected when the delegated token it was established with is replaced.
// `connectionToken` is that token: `null` while no connection was established
// (the socket is `lazy`) or once its reconnection was asked for, and
// `FAILED_HANDSHAKE` after a handshake that could not get a token, so the next
// token reconnects it.
const WS_OPEN = 1;
const FAILED_HANDSHAKE = '';
let connectionToken = null;
// Handshakes waiting for their token. Nothing is closed meanwhile: the mint a
// handshake asked for notifies its listeners BEFORE the handshake gets the
// token, and closing the socket then would lose the subscriptions the client
// queues to restart.
let pendingHandshakes = 0;

const subscriptionClient = new SubscriptionClient(process.env.REACT_APP_GRAPHQL__SUBSCRIPTION_URL, {
  reconnect: true,
  lazy: true,
  connectionParams: async () => {
    const asSede = getActiveSedeId() !== null;
    pendingHandshakes += 1;
    let token;
    try {
      token = await getEffectiveTokenForRequest();
      connectionToken = token || null;
    } catch (error) {
      connectionToken = FAILED_HANDSHAKE;
      throw error;
    } finally {
      pendingHandshakes -= 1;
    }
    // The token may have been replaced while this handshake waited for it.
    // Checked on a later task, once the client has finished the handshake
    // with these params. Never scheduled outside sede mode.
    // eslint-disable-next-line no-use-before-define
    if (asSede) setTimeout(reconnectIfTokenReplaced, 0);
    return {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        Connection: 'Upgrade',
        Upgrade: 'websocket',
      },
    };
  },
});

const wsLink = new WebSocketLink(subscriptionClient);

// Closes the socket only when it is OPEN and was established with a token
// other than the current one. Not forced, so the client reconnects and
// restarts its subscriptions. A socket that is still connecting, or was never
// opened, reads the token when it connects.
function reconnectIfTokenReplaced() {
  if (pendingHandshakes > 0) return;
  if (subscriptionClient.status !== WS_OPEN) return;
  if (connectionToken === null) return;
  const currentToken = peekEffectiveToken();
  if (currentToken === null || currentToken === connectionToken) return;
  // Once: the reconnection sets it again when it is established.
  connectionToken = null;
  subscriptionClient.close(false, false);
}

// Never fires outside sede mode.
onDelegatedTokenChange(reconnectIfTokenReplaced);

// `sedeCompanyId` records which sede the operation leaves for (null outside
// sede mode), so `sedeAuthRetryLink` knows whose failure a 401 is.
const authLink = setContext(async (_, { headers }) => {
  const sedeCompanyId = getActiveSedeId();
  const token = await getEffectiveTokenForRequest();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
    sedeCompanyId,
  };
});

// Link-level on purpose: most of the app calls `client.query` directly. An
// HTTP 401 on an operation sent as a sede is retried ONCE with a new delegated
// token; a second 401 leaves the sede. The normal session is never touched
// here, and operations sent outside sede mode pass through unchanged.
const sedeAuthRetryLink = new ApolloLink((operation, forward) => new Observable((observer) => {
  let subscription;
  let retried = false;

  const send = () => {
    subscription = forward(operation).subscribe({
      next: (result) => observer.next(result),
      complete: () => observer.complete(),
      error: (error) => {
        const { sedeCompanyId, headers } = operation.getContext();
        const rejectedAsCurrentSede = error?.statusCode === 401
          && Boolean(sedeCompanyId)
          && getActiveSedeId() === sedeCompanyId;
        if (!rejectedAsCurrentSede) {
          observer.error(error);
          return;
        }
        if (retried) {
          handleSedeAccessLost(SEDE_LOST_REASONS.AUTH_REJECTED);
          observer.error(error);
          return;
        }
        retried = true;
        discardRejectedDelegatedToken(headers?.authorization);
        send();
      },
    });
  };
  send();

  return () => {
    if (subscription) subscription.unsubscribe();
  };
}));

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

const apolloClient = new ApolloClient({
  link: ApolloLink.from([sentryApolloLink, sedeAuthRetryLink, authLink, link]),
  cache: new InMemoryCache(),
});

const apolloQuery = async (query, variables) => {
  try {
    const response = await apolloClient.query({ query, variables, fetchPolicy: 'no-cache' });
    return response;
  } catch (error) {
    if (error?.graphQLErrors?.[0]?.message === 'INVALID_TOKEN') {
      // Partner access: as a sede this leaves the sede, never the normal session.
      if (handleAuthFailureInSedeMode(SEDE_LOST_REASONS.INVALID_TOKEN)) return;
      localStorage.clear();
      window.location.replace('/');
    } else throw error;
  }
};

const apolloMutation = async (mutation, variables) => {
  try {
    const response = await apolloClient.mutate({ mutation, variables, fetchPolicy: 'no-cache' });
    return response;
  } catch (error) {
    if (error?.graphQLErrors?.[0]?.message === 'INVALID_TOKEN') {
      if (handleAuthFailureInSedeMode(SEDE_LOST_REASONS.INVALID_TOKEN)) return;
      localStorage.clear();
      window.location.replace('/');
    } else throw error;
  }
};

const client = {
  query: (operation) => apolloQuery(operation.query, operation.variables),
  mutate: (operation) => apolloMutation(operation.mutation, operation.variables),
  subscribe: (operation) => apolloClient.subscribe({ ...operation, fetchPolicy: 'no-cache' }),
};


export default client;
